import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import {
  LocalizedMessage,
  LocalizedPercentage,
  LocalizedPrice,
  standardDateNoSecondsOptions,
} from 'intl/LocalizedComponents';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _isFinite from 'lodash/isFinite';
import _isNil from 'lodash/isNil';
import _isNull from 'lodash/isNull';
import _map from 'lodash/map';
import _max from 'lodash/max';
import _reject from 'lodash/reject';
import _size from 'lodash/size';
import _without from 'lodash/without';
import PropTypes from 'prop-types';
// Permissions
import permissions from 'constants/Permissions';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withPermissions from 'hocs/WithPermissions';
import withStateReinit from 'hocs/WithStateReinit';
// Material UI
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
// Core components
import AppLoader from 'components/core/AppLoader';
import Avatar from 'components/core/Avatar';
import { TextButton, ContainedButton } from 'components/core/Button';
import Card from 'components/core/Card';
import Checkbox from 'components/core/Checkbox';
import DateTimePicker2 from 'components/core/DateTimePicker2/DateTimePicker2';
import Divider from 'components/core/Divider';
import List from 'components/core/List';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
// Models
import { sanitizeDesc } from 'models/accessWay';
import mBooking from 'models/cpm/booking';
import mCarpark from 'models/cpm/carpark';
import mCustomer from 'models/cpm/customer';
import mFinance from 'models/cpm/finance';
import mPriceRule from 'models/cpm/priceRule';
// Custom
import {
  cpmCustomerDataShape,
  cpmDoorDataShape,
  cpmParkingCategoryDataShape,
  errorShape,
  userOperationShape,
} from 'constants/PropTypesShapes';
import { cpmGetCarparkCustomer, cpmGetCarparkUniqueKeypadCode } from 'actions';
import { invoke, sanitizeInt, sanitizeFloatRounded } from 'utils/Tools';
import { dateGenerator, dateFormatter, breakDownDurationInMinutes } from 'utils/DateUtils';
import {
  buildFormErrors,
  cpmBookingAddConstraints,
  completeFormErrors,
  removeClientErrors,
  keypadCodeConstraints,
  licensePlateConstraints,
} from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import AppIcon from 'components/icons/AppIcon';
import { AddContainedButton } from 'components/buttons/ContainedButtons';
import { DeleteIconButton } from 'components/buttons/IconButtons';
import NumberInputField from 'components/inputFields/NumberInputField';
import InformationItem from 'components/items/InformationItem';
import Callout from 'components/misc/Callout';
import CustomerChooseList from 'cpmCarparks/assets/customers/CustomerChooseList';
import CustomerDesc from 'cpmCarparks/assets/customers/CustomerDesc';
import CarparkDoorItem from 'cpmCarparks/assets/main/CarparkDoorItem';
import ParkingCategoryTitle from 'cpmCarparks/assets/parkingCategories/ParkingCategoryTitle';
import Amount from 'components/finance/Amount';
import withMessageDialog from 'hocs/WithMessageDialog';
import { KEYPAD_CODE_MAX_LENGTH } from 'constants/App';
// Utils
import RegexUtils from 'utils/RegexUtils';

const StepIcon = ({ active, completed, icon }) => {
  const icons = {
    1: 'itemCustomer',
    2: 'itemInformation',
    3: 'itemAccessWay2',
    4: 'itemConfig',
  };

  return (
    <Avatar primary={active || completed} size={30} style={{ marginLeft: -3 }}>
      <AppIcon id={icons[icon]} />
    </Avatar>
  );
};

StepIcon.propTypes = {
  active: PropTypes.bool.isRequired,
  completed: PropTypes.bool.isRequired,
  icon: PropTypes.number.isRequired,
};

class BookingAddForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleNext = this.handleNext.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleStep = this.handleStep.bind(this);
    this.handleSelectCustomer = this.handleSelectCustomer.bind(this);
    this.handleGenerateOffer = this.handleGenerateOffer.bind(this);
    this.handleOfferFieldChange = this.handleOfferFieldChange.bind(this);
    this.handleKeypadCodeChange = this.handleKeypadCodeChange.bind(this);
    this.handleAddKeypadCode = this.handleAddKeypadCode.bind(this);
    this.handleDeleteKeypadCode = this.handleDeleteKeypadCode.bind(this);
    this.handleLicensePlateChange = this.handleLicensePlateChange.bind(this);
    this.handleAddVehicle = this.handleAddVehicle.bind(this);
    this.handleDeleteVehicle = this.handleDeleteVehicle.bind(this);
    this.handleToggleDoor = this.handleToggleDoor.bind(this);
    this.handleValidate = this.handleValidate.bind(this);

    const { fixedCustomer } = this.props;

    this.state = {
      scrollToErrors: false,
      stepIndex: fixedCustomer ? 1 : 0,
      completed: fixedCustomer ? 1 : 0,
      keypadCode: '',
      licensePlate: '',
    };
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const {
      onRef, fixedCustomer, doors, onFieldsChange,
    } = this.props;
    onRef(this);

    // Set customer if needed
    if (fixedCustomer) {
      onFieldsChange({ customer: fixedCustomer });
    }

    // By default, all doors are enabled
    onFieldsChange({
      doorsIds: _map(doors, 'id'),
    });
  }

  componentDidUpdate(prevProps) {
    const {
      id,
      customerAdd,
      addedCustomer,
      offerGenerate,
      uniqueKeypadCode,
      errors,
      fields: {
        sendPaymentLinkToCustomer,
        customer,
      },
      onFieldsChange,
      handleGetCustomer,
      onErrorsChange,
    } = this.props;
    const { scrollToErrors } = this.state;
    const {
      customerAdd: prevCustomerAdd,
      addedCustomer: prevAddedCustomer,
      offerGenerate: prevOfferGenerate,
      uniqueKeypadCode: prevUniqueKeypadCode,
      errors: prevErrors,
    } = prevProps;

    const offerResult = _get(offerGenerate, 'result[0]', null);
    const prevOfferResult = _get(prevOfferGenerate, 'result[0]', null);

    const customerHasEmail = Boolean(customer && customer.email);
    const thereIsNewOffer = (offerResult && !prevOfferResult) || (offerResult && prevOfferResult && offerResult.reference !== prevOfferResult.reference);
    const isPayingAnAmount = offerResult && !mFinance.offerIsFree(offerResult) && !mFinance.offerAmountToPayIsZero(offerResult);

    // Catch if a new customer was added to load it
    if (customerAdd.added && !prevCustomerAdd.added) {
      const { id: customerId } = customerAdd.result;
      handleGetCustomer({
        carparkId: id,
        id: customerId,
      });
    }
    // Catch end of add customer to select it and switch to next step
    if (addedCustomer && !prevAddedCustomer) {
      // Set customer
      onFieldsChange({
        customer: addedCustomer,
      });
      // Switch to next step
      invoke(() => {
        this.setState({
          stepIndex: 1,
        });
      });
    }

    if (thereIsNewOffer) {
      // Set sendPaymentLinkToCustomer to true in case it was disabled if customer has email and a new paying offer
      if (customerHasEmail && isPayingAnAmount && sendPaymentLinkToCustomer === false) {
        onFieldsChange({ sendPaymentLinkToCustomer: true });
      }

      // Set sendPaymentLinkToCustomer to false if offer made is free
      if (!isPayingAnAmount && sendPaymentLinkToCustomer === true) {
        onFieldsChange({ sendPaymentLinkToCustomer: false });
      }

      if (errors) {
        removeClientErrors(errors, ['begin', 'end', 'spacesQuantity']);
        onErrorsChange(errors);
      }
    }

    if (
      !offerGenerate.loading
      && prevOfferGenerate.loading
      && offerGenerate.result
      && _isEmpty(offerGenerate.result)
    ) {
      onErrorsChange(
        completeFormErrors(errors, 'spacesQuantity', ['noSpaceAvailable']),
      );
    }

    // Catch if a unique code has been generated
    if (uniqueKeypadCode.result && !prevUniqueKeypadCode.result) {
      const {
        fields: { keypadCodes },
      } = this.props;
      onFieldsChange({
        keypadCodes: [...keypadCodes, uniqueKeypadCode.result],
      });
    }

    // Catch errors
    if (errors && !prevErrors) {
      let stepIndex;
      if (
        _has(errors, 'customer')
      ) {
        stepIndex = 0;
      } else if (
        _has(errors, 'parkingCategoryId')
          || _has(errors, 'begin')
          || _has(errors, 'end')
          || _has(errors, 'price')
          || _has(errors, 'sendPaymentLinkToCustomer')
      ) {
        stepIndex = 1;
      } else if (_has(errors, 'keypadCodes') || _has(errors, 'vehicles')) {
        stepIndex = 2;
      }
      if (stepIndex) {
        // Switch to next step
        invoke(() => {
          this.setState({
            stepIndex,
          });
        });
      }
    }

    // Reset scroll to errors if there are errors
    if (scrollToErrors && !_isNull(errors)) {
      invoke(() => {
        this.setState({ scrollToErrors: false });
      });
    }
  }

  // Handlers
  handleNext() {
    this.setState((state) => ({
      stepIndex: state.stepIndex + 1,
      completed: _max([state.completed, state.stepIndex + 1]),
    }));
  }

  handlePrev() {
    this.setState((state) => ({
      stepIndex: state.stepIndex - 1,
    }));
  }

  handleStep(stepIndex) {
    this.setState({
      stepIndex,
    });
  }

  handleSelectCustomer(customer) {
    // Auto switch to next step when customer with email is selected
    if (customer && customer.email) {
      this.handleOfferFieldChange({ customer }, this.handleNext);
    }

    if (customer && !customer.email) {
      this.props.handleDisplayMessage('error.addBooking.customerHasNoEmail', {}, 'danger');
    }
  }

  handleGenerateOffer() {
    this.doGenerateOffer();
  }

  handleOfferFieldChange(changedField, callback) {
    const { onFieldsChange } = this.props;

    // First update concerned field, then generate an offer
    onFieldsChange(changedField, () => {
      if (callback) {
        callback();
      }

      this.doGenerateOffer();
    });
  }

  handleKeypadCodeChange(value) {
    this.setState({
      keypadCode: sanitizeDesc(value, 'keypad_code'),
    });
  }

  handleAddKeypadCode() {
    const { keypadCode } = this.state;
    const {
      fields: { keypadCodes },
      onErrorsChange,
      onFieldsChange,
    } = this.props;
    if (!_isEmpty(keypadCode) && !_includes(keypadCodes, keypadCode)) {
      // Check errors
      const errors = buildFormErrors({ keypadCode }, keypadCodeConstraints);
      onErrorsChange(errors);
      // Add keypad if no error
      if (!errors) {
        onFieldsChange({
          keypadCodes: [...keypadCodes, keypadCode],
        });
        this.setState({
          keypadCode: '',
        });
      }
    }
  }

  handleDeleteKeypadCode(keypadCode) {
    const {
      fields: { keypadCodes },
      onFieldsChange,
    } = this.props;
    // Add license plate to array of vehicles
    onFieldsChange({
      keypadCodes: _without(keypadCodes, keypadCode),
    });
  }

  handleLicensePlateChange(value) {
    this.setState({
      licensePlate: sanitizeDesc(value, 'license_plate'),
    });
  }

  handleAddVehicle() {
    const { licensePlate } = this.state;
    const {
      fields: { vehicles },
      onErrorsChange,
      onFieldsChange,
    } = this.props;
    if (
      !_isEmpty(licensePlate)
      && !_find(vehicles, ['licensePlate', licensePlate])
    ) {
      // Check errors
      const errors = buildFormErrors({ licensePlate }, licensePlateConstraints);
      onErrorsChange(errors);
      // Add vehicle if no error
      if (!errors) {
        onFieldsChange({
          vehicles: [...vehicles, { licensePlate }],
        });
        this.setState({
          licensePlate: '',
        });
      }
    }
  }

  handleDeleteVehicle(vehicle) {
    const {
      fields: { vehicles },
      onFieldsChange,
    } = this.props;
    // Add license plate to array of vehicles
    onFieldsChange({
      vehicles: _reject(vehicles, ['licensePlate', vehicle.licensePlate]),
    });
  }

  handleToggleDoor(doorId) {
    const {
      fields: { doorsIds },
      onFieldsChange,
    } = this.props;
    // Add or remove door from list
    onFieldsChange({
      doorsIds: _includes(doorsIds, doorId)
        ? _without(doorsIds, doorId)
        : [...doorsIds, doorId],
    });
  }

  // Handlers called by parent (= DialogItem)

  // addBooking
  handleValidate() {
    const {
      offerGenerate,
      fields: {
        automaticKeypadCodesQuantity,
        customer,
        spacesQuantity,
        begin,
        end,
        comments,
        keypadCodes,
        vehicles,
        insideQuantity,
        outsideQuantity,
        outsideQuantityPermanentExit,
        doorsIds,
        restrictedByPermanentExit,
        restrictedByAntipassbackEntrance,
        restrictedByAntipassbackExit,
        sendPaymentLinkToCustomer,
      },
      onErrorsChange,
      onValidate,
      forbidManualKeypadCodes,
    } = this.props;

    let errors = buildFormErrors(
      {
        begin,
        customer,
        end,
        insideQuantity,
        spacesQuantity,
      },
      cpmBookingAddConstraints,
    );

    if (_isEmpty(offerGenerate.result) && begin && end) {
      errors = completeFormErrors(errors, 'spacesQuantity', ['noSpaceAvailable']);
    }

    // Apply errors
    onErrorsChange(errors);

    // Check offer
    const offer = offerGenerate.result && offerGenerate.result[0];

    // Scroll to errors on result
    this.setState({ scrollToErrors: true });

    // No error => validate form
    if (!errors && offer) {
      onValidate(
        mBooking.serializeAdd(
          {
            automaticKeypadCodesQuantity: forbidManualKeypadCodes
              ? automaticKeypadCodesQuantity
              : null,
            comments,
            doors: [...doorsIds],
            keypadCodes: forbidManualKeypadCodes ? null : keypadCodes,
            offerReference: offer.reference,
            insideQuantity,
            outsideQuantity,
            outsideQuantityPermanentExit,
            restrictedByPermanentExit,
            restrictedByAntipassbackEntrance,
            restrictedByAntipassbackExit,
            sendPaymentLinkToCustomer,
            vehicles,
          },
        ),
      );
      return true;
    }

    // Errors: go to the appropriate step
    let stepIndex;
    if (_has(errors, 'details.customer')) {
      stepIndex = 0;
    } else if (
      _has(errors, 'details.parkingCategoryId')
      || _has(errors, 'details.begin')
      || _has(errors, 'details.discount')
      || _has(errors, 'details.end')
      || _has(errors, 'details.spacesQuantity')
      || !offer
    ) {
      stepIndex = 1;
    } else {
      stepIndex = 2;
    }
    this.setState({ stepIndex });

    return false;
  }

  // Private
  doGenerateOffer() {
    const {
      id,
      fields: {
        customer,
        discount,
        spacesQuantity,
        begin,
        end,
      },
      onGenerateOffer,
      timezone,
    } = this.props;
    // Check fields
    if ((_isNil(discount) || _isFinite(discount)) && _isFinite(spacesQuantity) && begin && end) {
      // Scroll to errors on result
      this.setState({ scrollToErrors: true });
      const beginInUTC = dateFormatter.toServerFormat(begin, timezone, { seconds: 0 });
      const endInUTC = dateFormatter.toServerFormat(end, timezone, { seconds: 0 });
      onGenerateOffer(
        mBooking.serializeSearch({
          begin: beginInUTC,
          end: endInUTC,
          carpark: {
            carparkId: id,
            customerId: customer.id,
            parkingCategoryId: customer.parkingCategory.id,
          },
          discount,
          spacesQuantity,
        }),
      );
    }
  }

  render() {
    const {
      stepIndex, completed, keypadCode, licensePlate, scrollToErrors,
    } = this.state;
    const {
      id,
      timezone,
      fixedCustomer,
      offerGenerate,
      parkingCategories,
      doors,
      uniqueKeypadCode,
      handleGetUniqueKeypadCode,
      fields: {
        customer,
        spacesQuantity,
        begin,
        end,
        discount,
        comments,
        automaticKeypadCodesQuantity,
        keypadCodes,
        vehicles,
        insideQuantity,
        outsideQuantity,
        doorsIds,
        restrictedByPermanentExit,
        restrictedByAntipassbackEntrance,
        sendPaymentLinkToCustomer,
        restrictedByAntipassbackExit,
      },
      errors,
      hasPermission,
      onFieldsChange,
      onAddCustomer,
      forbidManualKeypadCodes,
    } = this.props;

    // Find customer parking category
    const parkingCategory = customer
      ? _find(parkingCategories, ['id', customer.parkingCategory.id])
      : null;

    // Dates
    const beginDate = dateGenerator.dateFromTimezone(begin, timezone);
    const endDate = dateGenerator.dateFromTimezone(end, timezone);

    const DELAY_BEFORE_BOOK = 5; // minutes

    const nowDate = dateGenerator.dateFromTimezone(dateGenerator.now(), timezone);
    if (nowDate instanceof Date) nowDate.setMinutes(nowDate.getMinutes() + DELAY_BEFORE_BOOK + 1); // Add one minute to avoid issues when now is 10:00:00 and begin is 10:00:00 (not authorized)

    // Offer
    const { loading: offerGenerateLoading, result: offerGenerateResult } = offerGenerate;
    const offer = offerGenerateResult && offerGenerateResult[0];

    const isPayingAnAmount = offer && !mFinance.offerIsFree(offer) && !mFinance.offerAmountToPayIsZero(offer);
    const customerHasEmail = Boolean(customer && customer.email);
    const canManagerSendBookingPaymentLinkOnBookingCreation = Boolean(isPayingAnAmount && customerHasEmail);

    // Permissions
    const {
      pCpm: { parkingPriceDiscountWrite },
    } = permissions;

    return (
      <FormDialogTemplate errors={errors} scrollToErrors={scrollToErrors}>
        <div className="row">
          <div className="col-12">
            <Stepper
              activeStep={stepIndex}
              orientation="vertical"
              style={{ backgroundColor: 'rgba(0,0,0,0)' }}
            >
              <Step>
                <StepLabel StepIconComponent={StepIcon}>
                  {customer ? (
                    <div>
                      <CustomerDesc customer={customer} email={customer.email} />
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-between">
                      <LocalizedMessage id="title.chooseCarparkUser" />
                      <AddContainedButton
                        size="small"
                        onClick={onAddCustomer}
                      />
                    </div>
                  )}
                </StepLabel>
                <StepContent>
                  <div className="row">
                    {_has(errors, 'customer') && (
                      <div className="col-12 pb-2">
                        <Callout severity="error" withInfoIcon>
                          {errors.customer}
                        </Callout>
                      </div>
                    )}
                    <div className="col-12">
                      <CustomerChooseList
                        id={id}
                        timezone={timezone}
                        withOnDemandCategory={false}
                        fullWidth={false}
                        withChoose
                        permanentFiltersSpecs={[
                          { key: 'onDemand', value: false },
                        ]}
                        selectedCustomer={customer}
                        onSelectCustomer={this.handleSelectCustomer}
                      />
                    </div>
                    <div className="col-12 pt-3">
                      <ContainedButton
                        disabled={!customer}
                        label={<LocalizedMessage id="button.next" />}
                        onClick={this.handleNext}
                      />
                    </div>
                  </div>
                </StepContent>
              </Step>
              <Step>
                <StepButton
                  completed={completed > 0}
                  onClick={() => this.handleStep(1)}
                >
                  <StepLabel StepIconComponent={StepIcon}>
                    <LocalizedMessage id="title.generalInfo" />
                  </StepLabel>
                </StepButton>
                <StepContent>
                  <div className="d-flex">
                    <FormHelper
                      elements={[
                        {
                          title: <LocalizedMessage id="field.spacesQuantity" />,
                          content: (
                            <LocalizedMessage id="help.booking.formSpacesQuantity" />
                          ),
                        },
                        {
                          title: <LocalizedMessage id="field.discount" />,
                          content: (
                            <LocalizedMessage id="help.booking.formDiscount" />
                          ),
                        },
                        {
                          title: <LocalizedMessage id="field.period" />,
                          content: (
                            <LocalizedMessage id="help.booking.formPeriod" />
                          ),
                        },
                      ]}
                    />
                    <div style={{ flex: 1 }}>
                      <Card>
                        <div className="row">
                          <div className="col-12">
                            <NumberInputField
                              label={(
                                <LocalizedMessage
                                  id="field.spacesQuantityWithMax"
                                  values={{ max: '128' }}
                                />
                              )}
                              errorText={_get(errors, 'spacesQuantity')}
                              min={1}
                              max={128}
                              value={spacesQuantity}
                              required
                              sanitizer={sanitizeInt}
                              inputMaxWidth={70}
                              onChange={(value) => this.handleOfferFieldChange({
                                spacesQuantity: value,
                                outsideQuantity: value - insideQuantity,
                              })}
                            />
                          </div>
                          <div className="col-12">
                            <NumberInputField
                              readOnly={
                                !hasPermission({
                                  needed: parkingPriceDiscountWrite,
                                  permissionsOrContext: 'carparkPermissions',
                                })
                              }
                              label={(<LocalizedMessage id="field.discount" />)}
                              errorText={_get(errors, 'discount')}
                              min={0}
                              max={100}
                              value={discount}
                              sanitizer={sanitizeFloatRounded(2, true)}
                              adornments={(discount && { start: '%' }) || {}}
                              inputMaxWidth={70}
                              onChange={(value) => this.handleOfferFieldChange({
                                discount: value,
                              })}
                            />
                          </div>
                          <div className="col-12 pt-3">
                            <Typography type="subtitle2">
                              <LocalizedMessage id="field.period" />
                            </Typography>
                          </div>
                          <div className="col-12 col-lg-9 my-3">
                            <DateTimePicker2
                              name="begin"
                              calendarType="begin"
                              clearable
                              hideIcons
                              defaultDate={beginDate || endDate || nowDate}
                              minDate={nowDate}
                              maxDate={endDate}
                              errorText={_get(errors, 'begin')}
                              date={beginDate}
                              beginDate={beginDate}
                              endDate={endDate}
                              labelDateId="label.start"
                              placeholderId="placeholder.start"
                              calendarLabelId="calendarLabel.start"
                              onChange={(newBegin) => this.handleOfferFieldChange({ begin: newBegin })}
                            />
                          </div>
                          <div className="col-12 col-lg-9">
                            <DateTimePicker2
                              name="end"
                              calendarType="end"
                              clearable
                              hideIcons
                              defaultDate={endDate || beginDate || nowDate}
                              minDate={beginDate || nowDate}
                              errorText={_get(errors, 'end')}
                              date={endDate}
                              beginDate={beginDate}
                              endDate={endDate}
                              labelDateId="label.end"
                              placeholderId="placeholder.end"
                              calendarLabelId="calendarLabel.end"
                              onChange={(newEnd) => this.handleOfferFieldChange({ end: newEnd })}
                            />
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                  <div className="d-flex pt-3">
                    <FormHelper
                      elements={
                        offer ? ([
                          {
                            title: <LocalizedMessage id="field.bookingRate" />,
                            content: <LocalizedMessage id={mFinance.offerIsFree(offer) ? 'help.booking.formPriceFree' : 'help.booking.formPricePaying'} />,
                          },
                        ]) : (
                          []
                        )
                      }
                    />
                    <div style={{ flex: 1 }}>
                      <Card
                        title={(
                          <Typography type="subtitle2">
                            <LocalizedMessage id="field.bookingRate" />
                          </Typography>
                        )}
                      >
                        <div className="row">
                          {_has(errors, 'price.offerReference') && (
                            <div className="col-12 pb-3">
                              <Callout
                                severity="error"
                                action={(
                                  <TextButton
                                    label={
                                      <LocalizedMessage id="button.generate" />
                                    }
                                    size="small"
                                    inherit
                                    onClick={this.handleGenerateOffer}
                                  />
                                )}
                              >
                                {errors.price.offerReference}
                              </Callout>
                            </div>
                          )}
                          {offerGenerateLoading ? (
                            <div className="col-12 text-center">
                              <AppLoader />
                            </div>
                          ) : (
                            <div className="col-12">
                              {offer ? (
                                <React.Fragment>
                                  {mFinance.offerIsFree(offer) ? (
                                    <div>
                                      <Typography type="subtitle">
                                        <LocalizedMessage id="parking.priceType.free" />
                                      </Typography>
                                      <div className="pt-2">
                                        <em>
                                          <LocalizedMessage
                                            id="info.freeBooking"
                                            values={{
                                              parkingCategory: (
                                                <ParkingCategoryTitle
                                                  parkingCategory={
                                                    parkingCategory
                                                  }
                                                />
                                              ),
                                            }}
                                          />
                                        </em>
                                      </div>
                                    </div>
                                  ) : (
                                    <React.Fragment>
                                      {isPayingAnAmount && (
                                        <div style={{ marginBottom: '16px' }}>
                                          <Callout severity="warning">
                                            <LocalizedMessage id="info.bookingForbiddenExitUntilPayment" />
                                          </Callout>
                                        </div>
                                      )}
                                      <Card outlined defaultBackground>
                                        <div className="row pb-2 align-items-center">
                                          <div className="col-7">
                                            <em>
                                              <LocalizedMessage id="field.priceBase" />
                                            </em>
                                          </div>
                                          <div className="col-5 d-flex justify-content-end">
                                            <Typography type="keyInfo3">
                                              <LocalizedPrice
                                                currency={
                                                  offer.priceRule.currency
                                                }
                                                value={mPriceRule.displayAmount(
                                                  offer.amountInclTax,
                                                  offer.priceRule,
                                                )}
                                              />
                                            </Typography>
                                          </div>
                                        </div>
                                        <div className="row py-2 align-items-center">
                                          <div className="col-7">
                                            <em>
                                              <LocalizedMessage id="field.discountApplied" />
                                            </em>
                                          </div>
                                          <div className="col-5 d-flex justify-content-end">
                                            <Typography type="keyInfo3">
                                              <LocalizedPercentage percentage={offer.discount} />
                                            </Typography>
                                          </div>
                                        </div>
                                        <div className="row py-2 align-items-center">
                                          <div className="col-7">
                                            <em>
                                              <LocalizedMessage id="field.priceWithDiscount" />
                                            </em>
                                          </div>
                                          <div className="col-5 d-flex justify-content-end">
                                            <Typography type="keyInfo3">
                                              <LocalizedPrice
                                                currency={offer.priceRule.currency}
                                                value={mPriceRule.displayAmount(
                                                  offer.discountedAmountInclTax,
                                                  offer.priceRule,
                                                )}
                                              />
                                            </Typography>
                                          </div>
                                        </div>
                                        <div className="row py-2 align-items-center">
                                          <div className="col-7">
                                            <em>
                                              <LocalizedMessage id="field.serviceFees" />
                                            </em>
                                          </div>
                                          <div className="col-5 d-flex justify-content-end">
                                            <Typography type="keyInfo3">
                                              <Amount amount={offer.serviceFeeAmountInclTax} priceRule={offer.priceRule} />
                                            </Typography>
                                          </div>
                                        </div>
                                        <Divider variant="middle" />
                                        <div className="row pt-2 align-items-center">
                                          <div className="col-7">
                                            <em>
                                              <LocalizedMessage id="field.amountToBePaid" />
                                            </em>
                                          </div>
                                          <div className="col-5 d-flex justify-content-end">
                                            <Typography type="keyInfo3">
                                              <LocalizedPrice
                                                currency={offer.priceRule.currency}
                                                value={mPriceRule.displayAmount(
                                                  offer.totalAmountInclTax,
                                                  offer.priceRule,
                                                )}
                                              />
                                            </Typography>
                                          </div>
                                        </div>
                                      </Card>
                                      {canManagerSendBookingPaymentLinkOnBookingCreation && (
                                        <div className="row mt-3 px-3">
                                          {_has(errors, 'sendPaymentLinkToCustomer') && (
                                          <Callout severity="error" withInfoIcon>
                                            {errors.sendPaymentLinkToCustomer}
                                          </Callout>
                                          )}
                                          <div className="mt-2">
                                            <Checkbox
                                              label={(
                                                <div>
                                                  <LocalizedMessage id="field.sendPaymentLinkToCustomer" />
                                                  <strong>{customer.email}</strong>
                                                </div>
                                            )}
                                              checked={sendPaymentLinkToCustomer}
                                              onChange={(checked) => onFieldsChange({
                                                sendPaymentLinkToCustomer: checked,
                                              })}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  )}
                                </React.Fragment>
                              ) : (
                                <em>
                                  <LocalizedMessage id="info.enterBookingDates" />
                                </em>
                              )}
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  </div>
                  <div className="d-flex pt-3">
                    <FormHelper elements={[]} />
                    <div style={{ flex: 1 }}>
                      <Card>
                        <div className="row">
                          <div className="col-12">
                            <TextField
                              label={<LocalizedMessage id="field.comments" />}
                              value={comments}
                              errorText={_get(errors, 'comments')}
                              onChange={(value) => onFieldsChange({ comments: value })}
                            />
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                  <div className="pt-4 left-spaced-buttons d-flex">
                    {!fixedCustomer && (
                      <TextButton
                        label={<LocalizedMessage id="button.prev" />}
                        onClick={this.handlePrev}
                      />
                    )}
                    <ContainedButton
                      label={<LocalizedMessage id="button.next" />}
                      onClick={this.handleNext}
                    />
                  </div>
                </StepContent>
              </Step>
              <Step>
                <StepButton
                  completed={completed > 1}
                  onClick={() => this.handleStep(2)}
                >
                  <StepLabel StepIconComponent={StepIcon}>
                    <LocalizedMessage id="title.accessWaysCarpark" />
                  </StepLabel>
                </StepButton>
                <StepContent>
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex">
                        <FormHelper
                          elements={
                            forbidManualKeypadCodes
                              ? [
                                {
                                  title: (
                                    <LocalizedMessage id="field.accessCodes" />
                                  ),
                                  content: (
                                    <LocalizedMessage id="help.parking.formAccessCodesForbidKeypadCodes" />
                                  ),
                                },
                              ]
                              : [
                                {
                                  title: (
                                    <LocalizedMessage id="field.accessCodes" />
                                  ),
                                  content: (
                                    <LocalizedMessage id="help.parking.formAccessCodes" />
                                  ),
                                },
                                {
                                  content: (
                                    <LocalizedMessage id="help.parking.formGenerateUniqueCode" />
                                  ),
                                },
                              ]
                          }
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.accessCodes" />
                              </Typography>
                            )}
                          >
                            {_has(errors, 'keypadCodes') && (
                              <div className="col-12 px-0 pt-0 pb-3">
                                <Callout severity="error">{_get(errors, 'keypadCodes')}</Callout>
                              </div>
                            )}
                            <div className="row">
                              {forbidManualKeypadCodes ? (
                                <div className="col-12">
                                  <NumberInputField
                                    label={
                                      <LocalizedMessage id="field.keypadCodesQuantity" />
                                    }
                                    min={0}
                                    value={automaticKeypadCodesQuantity}
                                    required
                                    sanitizer={sanitizeInt}
                                    inputMaxWidth={70}
                                    onChange={(value) => onFieldsChange({
                                      automaticKeypadCodesQuantity: value,
                                    })}
                                  />
                                </div>
                              ) : (
                                <div className="col-12">
                                  <Card
                                    padding="dense"
                                    outlined
                                    title={(
                                      <Typography type="subtitle">
                                        <LocalizedMessage id="title.addAccessCode" />
                                      </Typography>
                                    )}
                                  >
                                    <div className="d-flex align-items-end flex-wrap justify-content-between">
                                      <div
                                        style={{ width: 200 }}
                                        className="pl-2 pb-1"
                                      >
                                        <TextField
                                          margin="none"
                                          label={
                                            <LocalizedMessage id="field.keypadCode" />
                                          }
                                          value={keypadCode}
                                          inputMaxWidth={150}
                                          maxLength={KEYPAD_CODE_MAX_LENGTH}
                                          errorText={_get(errors, 'keypadCode')}
                                          onChange={this.handleKeypadCodeChange}
                                          onEnterPress={
                                            this.handleAddKeypadCode
                                          }
                                        />
                                      </div>
                                      <ContainedButton
                                        label={
                                          <LocalizedMessage id="button.validate" />
                                        }
                                        size="small"
                                        onClick={this.handleAddKeypadCode}
                                      />
                                    </div>
                                    <div className="d-flex justify-content-center pt-3">
                                      <TextButton
                                        size="small"
                                        loading={uniqueKeypadCode.loading}
                                        label={
                                          <LocalizedMessage id="button.generateUniqueCode" />
                                        }
                                        onClick={() => handleGetUniqueKeypadCode({
                                          carparkId: id,
                                        })}
                                      />
                                    </div>
                                  </Card>
                                  <div className="pt-3">
                                    <Card
                                      padding="dense"
                                      outlined
                                      defaultBackground
                                      title={(
                                        <Typography type="subtitle">
                                          <LocalizedMessage id="title.accessCodesDefined" />
                                        </Typography>
                                      )}
                                    >
                                      <List disablePadding>
                                        {_map(keypadCodes, (k, index) => (
                                          <React.Fragment key={index}>
                                            <InformationItem
                                              light
                                              icon="cAwTypeKCode"
                                              value={k}
                                              rightIcon={(
                                                <DeleteIconButton
                                                  size="small"
                                                  onClick={() => this.handleDeleteKeypadCode(
                                                    k,
                                                  )}
                                                />
                                              )}
                                            />
                                            {index < _size(keypadCodes) - 1 && (
                                              <Divider variant="middle" />
                                            )}
                                          </React.Fragment>
                                        ))}
                                        {_isEmpty(keypadCodes) && (
                                          <div className="px-3">
                                            <em>
                                              <LocalizedMessage id="info.useFormAddAccessCode" />
                                            </em>
                                          </div>
                                        )}
                                      </List>
                                    </Card>
                                  </div>
                                </div>
                              )}
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.authorizedVehicles" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formVehicles" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.authorizedVehicles" />
                              </Typography>
                            )}
                          >
                            {_has(errors, 'vehicles') && (
                              <div className="col-12 px-0 pt-0 pb-3">
                                <Callout severity="error">{_get(errors, 'vehicles')}</Callout>
                              </div>
                            )}
                            <div className="row">
                              <div className="col-12">
                                <Card
                                  padding="dense"
                                  outlined
                                  title={(
                                    <Typography type="subtitle">
                                      <LocalizedMessage id="title.addVehicle" />
                                    </Typography>
                                  )}
                                >
                                  <div className="d-flex align-items-end flex-wrap justify-content-between">
                                    <div
                                      style={{ width: 200 }}
                                      className="pl-2 pb-1"
                                    >
                                      <TextField
                                        margin="none"
                                        label={
                                          <LocalizedMessage id="field.licensePlate" />
                                        }
                                        value={licensePlate}
                                        inputMaxWidth={150}
                                        errorText={_get(errors, 'licensePlate')}
                                        onChange={this.handleLicensePlateChange}
                                        onEnterPress={this.handleAddVehicle}
                                      />
                                    </div>
                                    <ContainedButton
                                      label={
                                        <LocalizedMessage id="button.validate" />
                                      }
                                      size="small"
                                      onClick={this.handleAddVehicle}
                                    />
                                  </div>
                                </Card>
                                <div className="pt-3">
                                  <Card
                                    padding="dense"
                                    outlined
                                    defaultBackground
                                    title={(
                                      <Typography type="subtitle">
                                        <LocalizedMessage id="title.vehiclesDefined" />
                                      </Typography>
                                    )}
                                  >
                                    <List disablePadding>
                                      {_map(vehicles, (vehicle, index) => (
                                        <React.Fragment key={index}>
                                          <InformationItem
                                            light
                                            icon="cAwTypeLPlate"
                                            value={vehicle.licensePlate}
                                            rightIcon={(
                                              <DeleteIconButton
                                                size="small"
                                                onClick={() => this.handleDeleteVehicle(
                                                  vehicle,
                                                )}
                                              />
                                            )}
                                          />
                                          {index < _size(vehicles) - 1 && (
                                            <Divider variant="middle" />
                                          )}
                                        </React.Fragment>
                                      ))}
                                      {_isEmpty(vehicles) && (
                                        <div className="px-3">
                                          <em>
                                            <LocalizedMessage id="info.useFormAddVehicle" />
                                          </em>
                                        </div>
                                      )}
                                    </List>
                                  </Card>
                                </div>
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 left-spaced-buttons d-flex">
                    <TextButton
                      label={<LocalizedMessage id="button.prev" />}
                      onClick={this.handlePrev}
                    />
                    <ContainedButton
                      label={<LocalizedMessage id="button.next" />}
                      onClick={this.handleNext}
                    />
                  </div>
                </StepContent>
              </Step>
              <Step>
                <StepButton
                  completed={completed > 2}
                  onClick={() => this.handleStep(3)}
                >
                  <StepLabel StepIconComponent={StepIcon}>
                    <LocalizedMessage id="title.configuration" />
                  </StepLabel>
                </StepButton>
                <StepContent>
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="title.initialState" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formInitialState" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="title.initialState" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              <div className="col-12 col-lg-6">
                                <NumberInputField
                                  label={
                                    <LocalizedMessage id="field.insideVehicles" />
                                  }
                                  errorText={_get(errors, 'insideQuantity')}
                                  min={0}
                                  value={insideQuantity}
                                  required
                                  sanitizer={sanitizeInt}
                                  inputMaxWidth={70}
                                  onChange={(value) => onFieldsChange({ insideQuantity: value })}
                                />
                              </div>
                              <div className="col-12 col-lg-6">
                                <NumberInputField
                                  label={
                                    <LocalizedMessage id="field.outsideVehicles" />
                                  }
                                  errorText={_get(errors, 'outsideQuantity')}
                                  min={0}
                                  value={outsideQuantity}
                                  sanitizer={sanitizeInt}
                                  inputMaxWidth={70}
                                  onChange={(value) => onFieldsChange({ outsideQuantity: value })}
                                />
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.antipassback" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formAntipassback" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.antipassback" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedEntrance" />
                                  }
                                  checked={restrictedByAntipassbackEntrance}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByAntipassbackEntrance: checked,
                                  })}
                                />
                              </div>
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedExit" />
                                  }
                                  checked={restrictedByAntipassbackExit}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByAntipassbackExit: checked,
                                  })}
                                />
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.doorsAuthorized" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formDoorsAuthorized" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.doorsAuthorized" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              {_map(mCarpark.sortDoors(doors), (door) => (
                                <div key={door.id} className="col-12">
                                  <CarparkDoorItem
                                    lightMode={false}
                                    withShadow={false}
                                    door={door}
                                    toggle={{
                                      toggled: _includes(doorsIds, door.id),
                                      onToggle: () => this.handleToggleDoor(door.id),
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 pt-3">
                      <div className="d-flex">
                        <FormHelper
                          elements={[
                            {
                              title: (
                                <LocalizedMessage id="field.restrictedByPermanentExit" />
                              ),
                              content: (
                                <LocalizedMessage id="help.parking.formPermanentExit" />
                              ),
                            },
                          ]}
                        />
                        <div style={{ flex: 1 }}>
                          <Card
                            title={(
                              <Typography type="subtitle2">
                                <LocalizedMessage id="field.options" />
                              </Typography>
                            )}
                          >
                            <div className="row">
                              <div className="col-12">
                                <Checkbox
                                  label={
                                    <LocalizedMessage id="field.restrictedByPermanentExit" />
                                  }
                                  checked={restrictedByPermanentExit}
                                  onChange={(checked) => onFieldsChange({
                                    restrictedByPermanentExit: checked,
                                  })}
                                />
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 left-spaced-buttons d-flex">
                    <TextButton
                      label={<LocalizedMessage id="button.prev" />}
                      onClick={this.handlePrev}
                    />
                  </div>
                </StepContent>
              </Step>
            </Stepper>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

BookingAddForm.propTypes = {
  intl: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timezone: PropTypes.string.isRequired,
  fixedCustomer: cpmCustomerDataShape,
  addedCustomer: cpmCustomerDataShape,
  uniqueKeypadCode: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    result: PropTypes.string,
  }).isRequired,
  parkingCategories: PropTypes.arrayOf(cpmParkingCategoryDataShape).isRequired,
  doors: PropTypes.arrayOf(cpmDoorDataShape).isRequired,
  customerAdd: PropTypes.shape({
    added: PropTypes.bool.isRequired,
    result: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  offerGenerate: userOperationShape.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  forbidManualKeypadCodes: PropTypes.bool.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  onAddCustomer: PropTypes.func,
  onGenerateOffer: PropTypes.func.isRequired,
  handleGetCustomer: PropTypes.func.isRequired,
  handleDisplayMessage: PropTypes.func.isRequired,
  handleGetUniqueKeypadCode: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
};

BookingAddForm.defaultProps = {
  fixedCustomer: null,
  addedCustomer: null,
  operationError: null,
  errors: null,
  onAddCustomer: null,
};

const mapFieldsToProps = {
  automaticKeypadCodesQuantity: {
    defaultValue: 1,
  },
  customer: {
    defaultValue: null,
  },
  parkingCategoryId: {
    defaultValue: null,
  },
  begin: {
    defaultValue: null,
  },
  end: {
    defaultValue: null,
  },
  spacesQuantity: {
    defaultValue: 1,
  },
  discount: {
    defaultValue: undefined,
  },
  comments: {
    defaultValue: '',
  },
  keypadCodes: {
    defaultValue: [],
  },
  vehicles: {
    defaultValue: [],
  },
  insideQuantity: {
    defaultValue: 0,
  },
  outsideQuantity: {
    defaultValue: 1,
  },
  outsideQuantityPermanentExit: {
    defaultValue: 0,
  },
  restrictedByPermanentExit: {
    defaultValue: false,
  },
  restrictedByAntipassbackEntrance: {
    defaultValue: false,
  },
  sendPaymentLinkToCustomer: {
    defaultValue: false,
  },
  restrictedByAntipassbackExit: {
    defaultValue: false,
  },
  doorsIds: {
    defaultValue: [],
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: {
    ...ownProps.offerGenerate.error,
    ...ownProps.operationError,
    messageValuesExtractors: {
      begin: [
        {
          key: 'date',
          pattern: RegexUtils.dateTime,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
      ],
      end: [
        {
          key: 'date',
          pattern: RegexUtils.dateTime,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
        {
          key: 'duration',
          pattern: RegexUtils.durationFollowedByMinutes,
          setTransform: breakDownDurationInMinutes,
        },
      ],
    },
  },
});

const mapStateToProps = (state) => ({
  addedCustomer: state.cpmCarparkCustomer.current.data,
  customerAdd: state.cpmCarparkCustomer.add,
  doors: state.cpmCarpark.current.data.doors,
  forbidManualKeypadCodes: state.cpmCarpark.current.data.settings.general.forbidManualKeypadCodes,
  parkingCategories: state.cpmCarparkParkingCategory.list.elements,
  offerGenerate: state.cpmCarparkBooking.search,
  operationError: state.cpmCarparkBooking.add.error,
  uniqueKeypadCode: state.cpmCarpark.uniqueKeypadCode,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  handleGetCustomer: (data) => {
    dispatch(
      cpmGetCarparkCustomer(
        mCustomer.queGetCustomer(ownProps.hasPermission),
        data,
      ),
    );
  },
  handleGetUniqueKeypadCode: (data) => {
    dispatch(
      cpmGetCarparkUniqueKeypadCode(mCarpark.queGetUniqueKeypadCode, data),
    );
  },
});

module.exports = compose(
  injectIntl,
  withPermissions,
  connect(mapStateToProps, mapDispatchToProps),
  withMessageDialog,
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withStateReinit(['addCarparkBooking', 'searchCarparkBookingOffer']),
)(BookingAddForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/bookings/BookingAddForm.js